import { StorageError } from '../../app/errors';
import { readLogRing } from '../../app/logger';
import type {
  AnnotationRow,
  CoachCardRow,
  FlagResolutionRow,
  HypothesisRow,
  LogEntryRow,
  MatchNoteRow,
  MatchRow,
  ObservationRow,
  OurPlayerRow,
  PlayerRow,
  SetPieceRow,
  SettingRow,
  TalkPointRow,
  TeamRow,
  TemplateRow,
} from '../../types';
import { deleteBlobs } from '../blobStore';
import { requireTransaction } from '../commit';
import { db, read, TABLE } from '../db';
import type { TableName } from '../db';
import { deleteAnnotationsForCascade } from './annotations';
import { deleteCoachCardsForCascade } from './coachCards';
import { deleteHypothesesForCascade } from './hypotheses';
import { deleteMatchesForCascade } from './matches';
import { deleteNotesForCascade } from './matchNotes';
import { deleteObservationsForCascade } from './observations';
import { deleteResolutionsForCascade } from './flagResolutions';
import { deleteOurPlayersForReplace } from './ourPlayers';
import { deleteSetPiecesForCascade } from './setPieces';
import { deleteTalkPointsForCascade } from './talkPoints';
import { deletePlayersForCascade, deleteTeamsForCascade } from './teams';

/**
 * **Whole-profile operations — the two BAK-1/BAK-2 need and nothing else.**
 *
 * Every other repo in this directory is scoped to an entity or to a match. These two are not:
 *
 *   - `readWholeProfile()` — BAK-1 is *"a full JSON dump plus all audio blobs"*, and §13.5's recovery
 *     export must succeed on a profile that is already damaged. Both mean **every row of every
 *     table**, not a walk from matches outward: a hypothesis whose match was deleted is exactly what
 *     OBS-2's orphan report exists to surface, and an export that reached it through its match would
 *     drop the evidence it was supposed to carry.
 *   - `planProfileWipe()` / `applyProfileWipe()` — BAK-2's *"replace-only in v1"*. Import-merge is a
 *     named non-goal (requirements §5), so the import empties the profile first, and this is the
 *     thing that empties it.
 *
 * ── The wipe follows `cascade.ts`'s discipline, for the same reason ──────────
 *
 * BAK-4: *"Deletes cascade per INV-8 behind a confirm step stating the counts of what will be
 * removed."* A replace-only import destroys strictly more than any single cascade, so it owes the
 * user the same honesty. `ProfileWipePlan.counts[t]` is `ids[t].length` **by construction** — the
 * number shown and the rows removed are one object, not two queries that could answer differently —
 * and `applyProfileWipe` re-plans inside the transaction and **aborts** if anything drifted.
 *
 * ── What the wipe does NOT remove, and why each is a decision ────────────────
 *
 * | Table | Removed | Why |
 * |---|---|---|
 * | every archivable table, `audioBlobs` included | yes | it is being replaced |
 * | `outbox` | yes | a queued push names a `scopeId` that is about to stop existing; the drainer would build an archive for a match that is gone (§scope.ts throws) or, worse, PUT an empty one over the server's good copy |
 * | `uiSession`, `activeMatch` | yes | both carry a `matchId`; SES-3's resume and §13.7's writer lock would point at a row that no longer exists. §17.4 excludes both from VER-7 for the same reason |
 * | `logEntries` | **no** | §17.4: the OBS-1 ring *"is mutated by the act of reading it"* — the import writes to it while importing. Clearing it would destroy the record of the most destructive operation the app performs, at the moment it is performed. Since B-22 the ring the logger actually writes lives in another **database** (see {@link ProfileSnapshot.logEntries}), so this exclusion is now true by construction as well as by decision |
 */

/**
 * Every **product** table `readWholeProfile` reads. The transaction scope, so the two cannot drift.
 *
 * `logEntries` is deliberately **not** here, and that is build unit B-22's correction rather than an
 * omission: the OBS-1 ring lives in the harness database (`app/logger.ts`'s `LOG_RING_STORE`), the
 * product store of the same name has never been written by anything, and a scope entry for a table
 * this transaction does not read is exactly the drift this list's existence is meant to prevent.
 * See {@link readWholeProfile} for where the ring is read instead.
 */
export const ARCHIVE_SOURCE_TABLES: readonly TableName[] = [
  TABLE.teams,
  TABLE.players,
  TABLE.matches,
  TABLE.hypotheses,
  TABLE.observations,
  TABLE.flagResolutions,
  TABLE.setPieces,
  TABLE.talkPoints,
  TABLE.matchNotes,
  TABLE.annotations,
  TABLE.coachCards,
  TABLE.ourPlayers,
  TABLE.audioBlobs,
  TABLE.templates,
  TABLE.settings,
];

/** Every table BAK-2's replace empties. See the table in the module note for the three exclusions. */
export const REPLACED_TABLES: readonly TableName[] = [
  TABLE.teams,
  TABLE.players,
  TABLE.matches,
  TABLE.hypotheses,
  TABLE.observations,
  TABLE.flagResolutions,
  TABLE.setPieces,
  TABLE.talkPoints,
  TABLE.matchNotes,
  TABLE.annotations,
  TABLE.coachCards,
  TABLE.ourPlayers,
  TABLE.audioBlobs,
  TABLE.templates,
  TABLE.settings,
  TABLE.outbox,
  TABLE.uiSession,
  TABLE.activeMatch,
];

/**
 * Audio metadata without the bytes.
 *
 * The export reads bytes one blob at a time through `ArchiveBlobReader` (§11.2), so a snapshot that
 * carried every `Blob` would hold the whole season's audio in memory to produce a JSON field. This
 * projection is also exactly `archive/fields.ts`'s structural `ArchiveAudioBlobMeta`, which is how
 * the two layers agree without importing each other (§3.1).
 */
export interface AudioBlobMeta {
  id: string;
  mime: string;
  bytes: number;
  complete: boolean;
  createdAt: number;
}

/** Every archivable row on the device, from one consistent snapshot. */
export interface ProfileSnapshot {
  teams: TeamRow[];
  players: PlayerRow[];
  matches: MatchRow[];
  hypotheses: HypothesisRow[];
  observations: ObservationRow[];
  flagResolutions: FlagResolutionRow[];
  setPieces: SetPieceRow[];
  talkPoints: TalkPointRow[];
  matchNotes: MatchNoteRow[];
  annotations: AnnotationRow[];
  coachCards: CoachCardRow[];
  ourPlayers: OurPlayerRow[];
  audioBlobs: AudioBlobMeta[];
  templates: TemplateRow[];
  settings: SettingRow[];
  /**
   * OBS-2 — *"Debug data is included in the backup zip."* Emitted only for a BAK-1 export (§11.7).
   *
   * **Read from the harness database, not from this one** (build unit B-22). `app/logger.ts` writes
   * the ring there; the product store of the same name has never been written by any code path, so
   * until B-22 this field was always empty in production and `debug/log.ndjson` was absent from
   * every real archive. `app/logger.ts`'s `LOG_RING_STORE` note carries the finding in full.
   */
  logEntries: LogEntryRow[];
}

/**
 * Reads every archivable row in **one** readonly transaction, then the OBS-1 ring beside it.
 *
 * One transaction because an archive built from a series of independently-timed reads describes a
 * moment that never existed: a recorder finalize landing between two of them produces an
 * `audioBlobs` row with no `Observation`, and OBS-2's orphan report would then be reporting the
 * export's own race.
 *
 * ── Why the ring is read OUTSIDE that transaction ────────────────────────────
 *
 * It is in a different **database** (B-22 — see {@link ProfileSnapshot.logEntries}), so no
 * transaction can span it and the product's. Reaching a second Dexie instance from inside a live
 * IndexedDB transaction makes Dexie spawn its own transaction on the other connection, and awaiting
 * that inside the first one is how an IndexedDB transaction auto-commits under a caller that still
 * intends to read from it. There is also nothing to gain: the consistency argument above is about
 * *referential* integrity between product rows, and no log entry references one — the ring is a
 * time-ordered append-only record whose own consistency is per-entry.
 *
 * @throws {StorageError} on a read failure, from either read.
 */
export async function readWholeProfile(): Promise<ProfileSnapshot> {
  const profile = await read(ARCHIVE_SOURCE_TABLES, collectProfile, 'archive source');
  return { ...profile, logEntries: await readLogRing() };
}

async function collectProfile(): Promise<Omit<ProfileSnapshot, 'logEntries'>> {
  const handle = db();
  try {
    const audioBlobs: AudioBlobMeta[] = [];
    // Projected during iteration rather than after `toArray()`: the point is never to hold the
    // bytes, and a `.map()` over a materialised array would have held them all first.
    await handle.audioBlobs.each((row) => {
      audioBlobs.push({
        id: row.id,
        mime: row.mime,
        bytes: row.bytes,
        complete: row.complete,
        createdAt: row.createdAt,
      });
    });

    return {
      teams: await handle.teams.toArray(),
      players: await handle.players.toArray(),
      matches: await handle.matches.toArray(),
      hypotheses: await handle.hypotheses.toArray(),
      observations: await handle.observations.toArray(),
      flagResolutions: await handle.flagResolutions.toArray(),
      setPieces: await handle.setPieces.toArray(),
      talkPoints: await handle.talkPoints.toArray(),
      matchNotes: await handle.matchNotes.toArray(),
      annotations: await handle.annotations.toArray(),
      coachCards: await handle.coachCards.toArray(),
      ourPlayers: await handle.ourPlayers.toArray(),
      audioBlobs,
      templates: await handle.templates.toArray(),
      settings: await handle.settings.toArray(),
    };
  } catch (cause) {
    throw new StorageError('Could not read the profile for a backup.', { cause });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// The two profile-scope writers BAK-2 needs (SYN-4's "Templates and Settings")
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Writes one `Template` — BAK-2's import, and PRE-2's first-run seed install.
 *
 * `add`, not `put`, for both. BAK-2 writes into a profile the same transaction has just emptied, so
 * a colliding id means the wipe did not happen — which must abort the import, not overwrite a row.
 * PRE-2's installer wants the same refusal for the opposite reason: it inserts only the ids
 * `listTemplates` says are missing, so a collision means two contexts seeded at once and the second
 * must abort rather than silently rewrite a library the user may have been reading.
 *
 * @throws {StorageError} outside a transaction, on a duplicate id, or on a write failure.
 */
export async function insertTemplate(row: TemplateRow): Promise<void> {
  requireTransaction('insertTemplate');
  try {
    await db().templates.add(row);
  } catch (cause) {
    throw new StorageError('Could not import the template.', {
      cause,
      context: { store: TABLE.templates, id: row.id },
    });
  }
}

/**
 * PRE-2 — the template library, for the Pre-Match library section and for the seed installer.
 *
 * Ordered by id so the library renders in a stable order across launches; the seed ids are literal
 * and stable for the same reason. `builtin` is not indexed (IndexedDB has no boolean key), so a
 * caller that wants only the built-ins filters in memory — the library is a few rows, not a table
 * worth an index.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listTemplates(): Promise<TemplateRow[]> {
  try {
    return await db().templates.orderBy('id').toArray();
  } catch (cause) {
    throw new StorageError('Could not read the template library.', { cause });
  }
}

/**
 * Writes one imported `Setting`.
 *
 * `add` for the same reason as `insertTemplate`. This module deliberately offers **no** typed setting
 * accessor: BAK-5's settings surface is build unit U-20's, and a general `putSetting` here would be
 * the second writer of a table whose one interesting property (§11.8's closed key space) depends on
 * every write being deliberate. (B-4 landed that surface under F-5's ruling as the `UpdateSetting`
 * command — `repos/settings.ts` is its writer, key-validated so the condition still holds.)
 *
 * @throws {StorageError} outside a transaction, on a duplicate key, or on a write failure.
 */
export async function insertSetting(row: SettingRow): Promise<void> {
  requireTransaction('insertSetting');
  try {
    await db().settings.add(row);
  } catch (cause) {
    throw new StorageError('Could not import the setting.', {
      cause,
      context: { store: TABLE.settings, key: row.key },
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// BAK-2's replace — planned, confirmed, and re-checked inside the transaction
// ─────────────────────────────────────────────────────────────────────────────

export type ProfileWipeCounts = Readonly<Record<TableName, number>>;

export interface ProfileWipePlan {
  /** Row ids per table, sorted, so two plans of the same data compare equal. */
  readonly ids: Readonly<Record<TableName, readonly string[]>>;
  /** Derived from `ids` — never a second query. This is BAK-4's confirm text. */
  readonly counts: ProfileWipeCounts;
  /** Everything that will go, for a one-line summary. */
  readonly total: number;
}

/**
 * Counts what a replace-only import would destroy. **Destroys nothing.**
 *
 * BAK-4's confirm input, at profile scale. Read in one readonly transaction so the counts describe
 * one moment.
 *
 * @throws {StorageError} on a read failure.
 */
export async function planProfileWipe(): Promise<ProfileWipePlan> {
  return read(REPLACED_TABLES, collectWipePlan, 'profile wipe plan');
}

/**
 * Empties the profile, joining the caller's transaction.
 *
 * Re-plans first and **aborts** if the database no longer matches what the user confirmed. Both
 * alternatives are silent failures: deleting by the stale plan leaves whatever arrived since, and
 * the import then writes into a profile that is not empty; deleting by a fresh plan removes more
 * than the confirm stated, which is precisely what BAK-4 forbids.
 *
 * @throws {StorageError} outside a transaction; if the profile changed since the plan was taken
 *   (`context.staleProfileWipe === true` — re-plan, re-confirm, retry); or on a write failure. Any
 *   throw aborts the transaction, so a half-emptied profile is not a reachable state.
 */
export async function applyProfileWipe(plan: ProfileWipePlan): Promise<void> {
  requireTransaction('applyProfileWipe');

  const fresh = await collectWipePlan();
  const drifted = REPLACED_TABLES.filter((table) => !sameIds(plan.ids[table], fresh.ids[table]));
  if (drifted.length > 0) {
    throw new StorageError(
      `The data changed since you were shown what importing would replace (${drifted.join(', ')}). Nothing was removed — check the counts again.`,
      {
        context: {
          staleProfileWipe: true,
          drifted,
          confirmed: plan.counts,
          actual: fresh.counts,
        },
      },
    );
  }

  const { ids } = fresh;
  const handle = db();

  // Leaves first, roots last — same ordering as `cascade.ts`, so anything reading mid-transaction
  // sees a consistent intermediate state. A throw anywhere aborts the whole thing regardless.
  await deleteObservationsForCascade(ids[TABLE.observations]);
  await deleteResolutionsForCascade(ids[TABLE.flagResolutions]);
  await deleteTalkPointsForCascade(ids[TABLE.talkPoints]);
  await deleteNotesForCascade(ids[TABLE.matchNotes]);
  await deleteAnnotationsForCascade(ids[TABLE.annotations]);
  await deleteCoachCardsForCascade(ids[TABLE.coachCards]);
  await deleteSetPiecesForCascade(ids[TABLE.setPieces]);
  await deleteHypothesesForCascade(ids[TABLE.hypotheses]);
  await deleteBlobs(ids[TABLE.audioBlobs]);
  await deleteMatchesForCascade(ids[TABLE.matches]);
  await deletePlayersForCascade(ids[TABLE.players]);
  await deleteTeamsForCascade(ids[TABLE.teams]);
  // B-1: the roster is being replaced like `templates` — profile data, no cascade owner.
  await deleteOurPlayersForReplace(ids[TABLE.ourPlayers]);

  try {
    await handle.templates.bulkDelete([...ids[TABLE.templates]]);
    await handle.settings.bulkDelete([...ids[TABLE.settings]]);
    // Device-local bookkeeping, cleared wholesale. `outbox`'s primary key is the compound
    // `[kind+scopeId]`, so its ids are not single strings — `clear()` is the honest expression of
    // "none of this survives the profile it describes" for all three.
    await handle.outbox.clear();
    await handle.uiSession.clear();
    await handle.activeMatch.clear();
  } catch (cause) {
    throw new StorageError('Could not clear the profile for a replace-only import.', { cause });
  }
}

/**
 * Proves the committed row counts are the ones the caller was told about.
 *
 * `applyImport` reports "what was written". Without this that number is the *plan's*, re-presented —
 * true by construction while every write is one-to-one and any divergence aborts, but a proxy for
 * the thing it names rather than a measurement of it. Counting inside the transaction makes it a
 * measurement, and turns a writer that silently dropped a row (a `put` that overwrote, a
 * `writeSelection` that renumbered one away) into an abort instead of a wrong number in a report.
 *
 * @param expected row counts per table. Tables absent from the map are not checked.
 * @throws {StorageError} naming every table whose count differs.
 */
export async function assertWrittenCounts(
  expected: Readonly<Partial<Record<TableName, number>>>,
): Promise<void> {
  requireTransaction('assertWrittenCounts');
  const handle = db();
  const wrong: string[] = [];
  for (const [table, count] of Object.entries(expected) as [TableName, number][]) {
    const actual = await handle.table(table).count();
    if (actual !== count) wrong.push(`${table}: expected ${count}, wrote ${actual}`);
  }
  if (wrong.length > 0) {
    throw new StorageError(
      `The import wrote a different number of rows than it planned to (${wrong.join(', ')}). Nothing was committed.`,
      { context: { wrong } },
    );
  }
}

/**
 * Proves the profile really is empty, inside the transaction, before anything is written.
 *
 * BAK-2's replace-only is otherwise a property of the *sequence* — wipe, then write — and a sequence
 * is not a mechanism. This is the postcondition that makes it one: if a table still holds rows the
 * transaction aborts and nothing commits, so an import can never half-merge into existing data.
 *
 * @throws {StorageError} listing every table that is not empty.
 */
export async function assertProfileEmpty(): Promise<void> {
  requireTransaction('assertProfileEmpty');
  const handle = db();
  const remaining: string[] = [];
  for (const table of REPLACED_TABLES) {
    const count = await handle.table(table).count();
    if (count > 0) remaining.push(`${table}: ${count}`);
  }
  if (remaining.length > 0) {
    throw new StorageError(
      `BAK-2 is replace-only, and the profile is not empty (${remaining.join(', ')}). Nothing was imported.`,
      { context: { remaining } },
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────

async function collectWipePlan(): Promise<ProfileWipePlan> {
  const handle = db();
  const ids = {} as Record<TableName, string[]>;
  const counts = {} as Record<TableName, number>;

  try {
    for (const table of REPLACED_TABLES) {
      // `primaryKeys()` rather than `toArray()`: a plan should not have to load a season of audio
      // to count it. `outbox`'s compound key stringifies; it is used only for comparison.
      const keys = await handle.table(table).toCollection().primaryKeys();
      const list = keys.map((key) => String(key)).sort();
      ids[table] = list;
      counts[table] = list.length;
    }
  } catch (cause) {
    throw new StorageError('Could not work out what a replace-only import would remove.', { cause });
  }

  return {
    ids,
    counts,
    total: REPLACED_TABLES.reduce((sum, table) => sum + (counts[table] ?? 0), 0),
  };
}

function sameIds(confirmed: readonly string[] | undefined, actual: readonly string[]): boolean {
  if (!confirmed) return actual.length === 0;
  return confirmed.length === actual.length && confirmed.every((id, i) => id === actual[i]);
}
